import { Checkbox, InputLabel, TextField, Grid } from "@mui/material";
import { Col,Row, Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DialogProps } from "../../types/SimpleSearchModel";
import ConfirmationDialog from "../SimpleSearch/ConfirmationDialog";
import { useLoader } from "../../context/LoaderContext";
import { performanceReportAddEdit } from "../../api/reportApi/reportApi";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";
import { appSettings } from "../../config/appSetting";
interface EditReportDataProps {
  editReportData: any[];
  performanceReportCallback: () => void;
}

const ScheduleReport: React.FC<EditReportDataProps> = ({
  editReportData,
  performanceReportCallback,
}) => {
  const [isActive, setIsActive] = useState(editReportData[0]?.active);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedRepeat, setSelectedRepeat] = useState("1");
  const [repeatValue, setRepeatValue] = useState(1);
  const [checkboxStates, setCheckboxStates] = useState({
    downsample: false,
    print: false,
    prework: false,
    webEDelivery: false,
    printEDelivery: false,
  });

  const reportQueueTypeMapping: any = {
    downsample: 1,
    print: 2,
    prework: 4,
    webEDelivery: 8,
    printEDelivery: 16,
  };

  const getSelectedReportQueueTypeData = () => {
    return Object.entries(checkboxStates)
      .filter(([_, isChecked]) => isChecked)
      .map(([key]) => ({ id: reportQueueTypeMapping[key] }));
  };

  function getSelectedQueueTypes(reportQueueType: number) {
    const selected: string[] = [];
    for (const [key, value] of Object.entries(reportQueueTypeMapping)) {
      const numValue = value as number;
      if ((reportQueueType & numValue) === numValue) {
        selected.push(key);
      }
    }
    return selected;
  }

  const selectedTypes = getSelectedQueueTypes(
    editReportData[0]?.reportQueueType
  );
  const handleCheckboxChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckboxStates((prev) => ({
        ...prev,
        [key]: e.target.checked,
      }));
    };

  const [formData, setFormData] = useState({
    templateName: "",
    toEmail: "",
    bccEmail: "",
    ccEmail: "",
  });
  const [formErrors, setFormErrors] = useState({
    templateName: "",
    toEmail: "",
    bccEmail: "",
    ccEmail: "",
  });
  const validateEmail = (
    emailValue: string,
    emailType: "toEmail" | "ccEmail" | "bccEmail"
  ) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.exec(emailValue)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [emailType]: "Please enter a valid email address.",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [emailType]: "",
      }));
    }
  };

  const validateText = (text: string, fieldName: keyof typeof formErrors) => {
    const textPattern = /^[a-zA-Z\s]+$/;
    if (!textPattern.exec(text)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Please enter alphabets only",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    }
  };

  const daysArray = [
    { id: 1, day: "S" },
    { id: 2, day: "M" },
    { id: 4, day: "T" },
    { id: 8, day: "W" },
    { id: 16, day: "T" },
    { id: 32, day: "F" },
    { id: 64, day: "S" },
  ];

  const repeatOnValue = editReportData[0]?.repeatOn;
  const [repeatOn, setRepeatOn] = useState<number[]>(() => {
    if (Array.isArray(repeatOnValue)) {
      return repeatOnValue;
    }
    if (typeof repeatOnValue === "number") {
      return daysArray
        .filter((dayObj) => (repeatOnValue & dayObj.id) === dayObj.id)
        .map((dayObj) => dayObj.id);
    }
    return [];
  });

  const ScheduleData = [
    { id: 1, record: "Daily" },
    { id: 2, record: "Every weekday (Monday to Friday)" },
    { id: 3, record: "Every Monday, Wednesday, and Friday" },
    { id: 4, record: "Every Tuesday and Thursday" },
    { id: 5, record: "Weekly" },
    { id: 6, record: "Bi-Weekly (1st & 16th)" },
    { id: 7, record: "Monthly" },
    { id: 8, record: "Yearly" },
  ];

  const ScheduleDay = [
    { id: 1, record: 1 },
    { id: 2, record: 2 },
    { id: 3, record: 3 },
    { id: 4, record: 4 },
    { id: 5, record: 5 },
    { id: 6, record: 6 },
    { id: 7, record: 7 },
    { id: 8, record: 8 },
    { id: 9, record: 9 },
    { id: 10, record: 10 },
    { id: 11, record: 11 },
    { id: 12, record: 12 },
    { id: 13, record: 13 },
    { id: 14, record: 14 },
    { id: 15, record: 15 },
  ];

  const handleRepeatChange = (e: any) => {
    setSelectedRepeat(e.target.value);
  };

  const handleRepeatValueChange = (e: any) => {
    setRepeatValue(e.target.value);
  };

  const handleDayChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    dayId: number
  ) => {
    if (e.target.checked) {
      setRepeatOn((prev) => [...prev, dayId]);
    } else {
      setRepeatOn((prev) => prev.filter((id) => id !== dayId));
    }
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "bccEmail" || name === "ccEmail" || name === "toEmail") {
      validateEmail(value, name);
    }
    if (name === "templateName") validateText(value, name);
  };

  useEffect(() => {
    if (editReportData?.[0]) {
      setFormData({
        templateName: editReportData[0]?.reportName || "",
        toEmail: editReportData[0]?.toEmail || "",
        bccEmail: editReportData[0]?.bccEmail || "",
        ccEmail: editReportData[0]?.ccEmail || "",
      });
      const startDateRaw = editReportData[0]?.startDate;
      const endDateRaw = editReportData[0]?.endDate;
      setStartDate(startDateRaw ? new Date(startDateRaw) : null);
      setEndDate(endDateRaw ? new Date(endDateRaw) : null);
      setIsActive(editReportData[0]?.active);
      setSelectedRepeat(editReportData[0]?.repeatType.toString());
      const repeatOnValue = editReportData[0]?.repeatOn;
      if (Array.isArray(repeatOnValue)) {
        setRepeatOn(repeatOnValue);
      } else if (typeof repeatOnValue === "number") {
        const selectedIds = daysArray
          .filter((day) => (repeatOnValue & day.id) === day.id)
          .map((day) => day.id);
        setRepeatOn(selectedIds);
      } else {
        setRepeatOn([]);
      }
      const reportQueueType = editReportData[0]?.reportQueueType;
      const selectedTypes = getSelectedQueueTypes(reportQueueType);
      const updatedCheckboxStates = {
        downsample: selectedTypes.includes("downsample"),
        print: selectedTypes.includes("print"),
        prework: selectedTypes.includes("prework"),
        webEDelivery: selectedTypes.includes("webEDelivery"),
        printEDelivery: selectedTypes.includes("printEDelivery"),
      };
      setCheckboxStates(updatedCheckboxStates);
    }
  }, [editReportData]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogProps>({
    dialogTitle: "",
    contentText: "",
    confirmText: "",
    closeText: "",
    onConfirm: null,
  });

  const handleCancelClick = () => {
    setDialogProps({
      dialogTitle: "PerformanceReport - Create Schedular",
      contentText: "Are you sure you want to cancel?",
      confirmText: "Yes",
      closeText: "No",
      onConfirm: async () => {
        performanceReportCallback();
        setDialogOpen(false);
      },
    });
    setDialogOpen(true);
  };
  const { setLoading } = useLoader();
  const { showSnackbar } = useSnackbar();
  const handleUpdateClick = async () => {
    setLoading(true);
    try {
      const parseToDate = (value: any): Date | null => {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        return isNaN(date.getTime()) ? null : date;
      };
      const formatDate = (dateInput: any): string => {
        const date = parseToDate(dateInput);
        if (!date) {
          console.error("Invalid date input:", dateInput);
          return "";
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      };
      if (!startDate || !endDate) {
        console.error("Start Date or End Date is null");
        return;
      }
      const selectedReportQueueTypedata = getSelectedReportQueueTypeData();
      const schedulerobj = {
        ScheduleReportId: editReportData[0]?.scheduleReportId || 0,
        ReportName: formData.templateName,
        ToEmail: formData.toEmail ?? null,
        CCEmail: formData.ccEmail ?? null,
        BCCEmail: formData.bccEmail === "" ? null : formData.bccEmail,
        RepeatType: Number(selectedRepeat),
        RepeatFrequency: repeatValue,
        RepeatOndata: repeatOn.map((id) => ({ id })),
        RepeatOn: 0,
        StartDate: formatDate(startDate),
        EndDate: formatDate(endDate),
        ReportQueueTypedata: selectedReportQueueTypedata || selectedTypes,
        CreatedBy: Number(LocalStorageUtil.getItem("userId")),
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        Timezone:appSettings.timezone,
        NextRunDate: editReportData[0]?.nextRunDate || null,
        Active: isActive,
      };
      const param: any = {
        contextName: LocalStorageUtil.getItem("contextName"),
        schedulerobj: schedulerobj,
      };
      await performanceReportAddEdit(param);
      showSnackbar("Report-Scheduler updated Successfully");
      performanceReportCallback();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginLeft: "2em" }}>
      <div className="ag-theme-quartz-dark">
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Template Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter the Template Name"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleChange}
                  isInvalid={!!formErrors.templateName}
                  required
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.templateName && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.templateName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={9} style={{ display: "flex", justifyContent: "flex-end" }}>
              {editReportData[0] && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: isActive
                      ? "Primary"
                      : "var(--button-background-color)",
                  }}
                  className="active-button"
                  onClick={() => setIsActive(!isActive)}
                >
                  <Checkbox
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    sx={{
                      padding: "0",
                      marginRight: "8px",
                      color: "var(--text-color)",
                    }}
                  />
                  {isActive ? "ACTIVE" : "IN-ACTIVE"}
                </Button>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  To Email ID<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter the To Email ID"
                  name="toEmail"
                  value={formData.toEmail}
                  onChange={handleChange}
                  isInvalid={!!formErrors.toEmail}
                  required
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.toEmail && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.toEmail}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  CC Email ID
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter the CC Email ID"
                  name="ccEmail"
                  value={formData.ccEmail}
                  onChange={handleChange}
                  isInvalid={!!formErrors.ccEmail}
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.ccEmail && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.ccEmail}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  BCC Email ID
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter the BCC Email ID"
                  name="bccEmail"
                  value={formData.bccEmail}
                  onChange={handleChange}
                  isInvalid={!!formErrors.bccEmail}
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.bccEmail && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.bccEmail}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formRepeat">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Repeats<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="repeatFrequency"
                  value={selectedRepeat}
                  onChange={handleRepeatChange}
                  required
                  className="input-box"
                  style={{ fontSize: "11px" }}
                >
                  {ScheduleData.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.record}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              {["1", "5", "7", "8"].includes(selectedRepeat) && (
                <Form.Group controlId="formRepeatValue">
                  <Form.Label
                    className="input-labels"
                    style={{
                      color: "var(--aggrid-text-color)",
                      fontSize: "11px",
                    }}
                  >
                    Repeat Every<span className="required">*</span>
                  </Form.Label>
                  <Form.Select
                    name="repeatValue"
                    value={repeatValue}
                    onChange={handleRepeatValueChange}
                    required
                    className="select-dropdown"
                    style={{ fontSize: "11px" }}
                  >
                    {ScheduleDay.map((option) => (
                      <option key={option.id} value={option.record}>
                        {option.record}
                      </option>
                    ))}
                  </Form.Select>
                  {selectedRepeat === "1" && (
                    <span
                      style={{
                        color: "var(--aggrid-text-color)",
                        fontSize: "11px",
                      }}
                    >
                      days
                    </span>
                  )}
                  {selectedRepeat === "5" && (
                    <span
                      style={{
                        color: "var(--aggrid-text-color)",
                        fontSize: "11px",
                      }}
                    >
                      weeks
                    </span>
                  )}
                  {selectedRepeat === "7" && (
                    <span
                      style={{
                        color: "var(--aggrid-text-color)",
                        fontSize: "11px",
                      }}
                    >
                      months
                    </span>
                  )}
                  {selectedRepeat === "8" && (
                    <span
                      style={{
                        color: "var(--aggrid-text-color)",
                        fontSize: "11px",
                      }}
                    >
                      years
                    </span>
                  )}
                </Form.Group>
              )}
            </Col>
            <Col md={3}>
              {selectedRepeat === "5" && (
                <>
                  <Form.Label
                    className="input-labels"
                    style={{
                      color: "var(--aggrid-text-color)",
                      fontSize: "11px",
                    }}
                  >
                    Repeated On<span className="required">*</span>
                  </Form.Label>
                  <div
                    className="days-checkboxes"
                    style={{ display: "flex", gap: "10px" }}
                  >
                    {daysArray.map((day) => (
                      <Form.Check
                        key={day.id}
                        type="checkbox"
                        label={day.day}
                        name={`day_${day.day}`}
                        className="day-checkbox"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "11px",
                          color: "var(--aggrid-text-color)",
                          gap: "5px",
                        }}
                        checked={
                          Array.isArray(repeatOn) && repeatOn.includes(day.id)
                        }
                        onChange={(e) => handleDayChange(e, day.id)}
                      />
                    ))}
                  </div>
                </>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3} xs={12}>
              <InputLabel
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "var(--text-color)",
                }}
              >
                From Date: *
              </InputLabel>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                showTimeSelect
                timeIntervals={30}
                minDate={new Date()}
                timeFormat="HH:mm"
                dateFormat="yyyy-MM-dd HH:mm"
                required
                customInput={
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"                   
                    sx={{
                      height: "30px", 
                      backgroundColor: "white",
                      borderRadius: "4px",
                      width: "20vw",
                      "& .MuiOutlinedInput-root": {
                        height: "30px", 
                        "& input": {
                          fontSize: "11px", 
                          padding: "8px 10px",
                        },
                      },
                    }}
                  />
                }
              />
            </Col>
            <Col md={3}>
              <InputLabel
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "var(--text-color)",
                }}
              >
                To Date: *
              </InputLabel>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                showTimeSelect
                timeIntervals={30}
                minDate={new Date()}
                timeFormat="HH:mm"
                dateFormat="yyyy-MM-dd HH:mm"
                required
                customInput={
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{
                      height: "30px",
                      backgroundColor: "white",
                      borderRadius: "4px", 
                      width: "20vw",
                      "& .MuiOutlinedInput-root": {
                        height: "30px",
                        "& input": {
                          fontSize: "11px",
                          padding: "8px 10px", 
                        },
                      },
                    }}
                  />
                }
              />
            </Col>
          </Row>
          <Grid
            container
            spacing={2}
            sx={{ marginTop: "1em", marginLeft: ".1em" }}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              style={{ paddingTop: "0px", paddingLeft: "0px" }}
            >
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Repeat Every<span className="required">*</span>
              </Form.Label>
            </Grid>
            <Button
              variant="contained"
              size="small"
              className="checkbox-button"
            >
              <Checkbox
                className="custom-checkbox-wrapper"
                checked={checkboxStates.downsample}
                onChange={handleCheckboxChange("downsample")}
                sx={{
                  padding: "0",
                  marginRight: ".8em",
                  color: "white",
                }}
              />
              DOWNSAMPLE
            </Button>
            <Button
              variant="contained"
              size="small"
              className="checkbox-button"
            >
              <Checkbox
                className="custom-checkbox-wrapper"
                checked={checkboxStates.print}
                onChange={handleCheckboxChange("print")}
                sx={{
                  padding: "0",
                  marginRight: ".8em",
                  color: "white",
                }}
              />
              PRINT
            </Button>
            <Button
              variant="contained"
              size="small"
              className="checkbox-button"
            >
              <Checkbox
                className="custom-checkbox-wrapper"
                checked={checkboxStates.prework}
                onChange={handleCheckboxChange("prework")}
                sx={{
                  padding: "0",
                  marginRight: ".8em",
                  color: "white",
                }}
              />
              PREWORK
            </Button>
            <Button
              variant="contained"
              size="small"
              className="checkbox-button"
            >
              <Checkbox
                className="custom-checkbox-wrapper"
                checked={checkboxStates.webEDelivery}
                onChange={handleCheckboxChange("webEDelivery")}
                sx={{
                  padding: "0",
                  marginRight: ".8em",
                  color: "white",
                }}
              />
              WEB EDELIVERY
            </Button>
            <Button
              variant="contained"
              size="small"
              className="checkbox-button"
            >
              <Checkbox
                className="custom-checkbox-wrapper"
                checked={checkboxStates.printEDelivery}
                onChange={handleCheckboxChange("printEDelivery")}
                sx={{
                  padding: "0",
                  marginRight: ".8em",
                  color: "white",
                }}
              />
              PRINT EDELIVERY
            </Button>
          </Grid>
          <Grid
            item
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            {editReportData[0] ? (
              <Button
                variant="contained"
                className="update-button"
                onClick={handleUpdateClick}
              >
                Update
              </Button>
            ) : (
              <Button
                variant="contained"
                className="update-button"
                onClick={handleUpdateClick}
              >
                Create
              </Button>
            )}
            <Button
              variant="contained"
              className="cancel-button"
              style={{ marginRight: "1em" }}
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
          </Grid>
        </Form>
        <ConfirmationDialog
          open={dialogOpen}
          onConfirm={dialogProps.onConfirm}
          onClose={setDialogOpen.bind(this, false)}
          contentText={dialogProps.contentText}
          closeText={dialogProps.closeText}
          confirmText={dialogProps.confirmText}
          dialogTitle={dialogProps.dialogTitle}
        />
      </div>
    </div>
  );
};
export default ScheduleReport;
